import type React from "react";
import {memo, useCallback, useMemo} from "react";

import ImageUploadPreview from "../ImageUploadPreview";
import {SortableItem, SortableList} from "../SortableList";
import {Box} from "../ui/box";
import {VStack} from "../ui/vstack";

import Placeholder from "./Placeholder";
import type {UploadImageAreaProps} from "./types";
import UploadImagePlaceholder from "./UploadImagePlaceholder";
import UploadPlaceholder from "./UploadPlaceholder";
import {
    filterAttachmentOrFileType,
    filterAttachmentOrNativeImage,
    isAttachment,
    isFileType,
    isNativeImage
} from "./util";

type ImageListProps = UploadImageAreaProps & {
    isDragAccept?: boolean;
    onRemoveAttachment?: (url: string) => void;
    onResizeImage?: (image: {name: string; blob: Blob}) => void;
    onChangeTags?: (file: File | string, tags: string[]) => void;
    onOpen: () => void;
};

const ImageList: React.FC<ImageListProps> = ({
    attachments: attachmentOrFileList,
    uploading,
    tagOptions,
    canReorder,
    size = "md",
    maxImages = 5,
    isDragAccept,
    isNative,
    onRemoveAttachment,
    onRemoveImageFile,
    onResizeImage,
    onChangeTags,
    onSortEnd,
    onOpen
}) => {
    const numOfImages = attachmentOrFileList?.length ?? 0;
    const numOfImagesLeft = maxImages - numOfImages;

    const handleRemoveImage = useCallback(
        (image: File | string) => {
            if (isNative) {
                const imageFile = attachmentOrFileList?.find(item => {
                    if (isNativeImage(item)) {
                        return item.uri === image;
                    }
                    return false;
                });
                if (imageFile && isNativeImage(imageFile)) {
                    onRemoveImageFile?.(imageFile);
                }
            } else if (typeof image !== "string") {
                onRemoveImageFile?.(image);
            }
        },
        [attachmentOrFileList, isNative, onRemoveImageFile]
    );

    const imageListNode = (
        <Box
            className={`
                flex-row
                flex-wrap
                items-start
                gap-4
            `}
        >
            {attachmentOrFileList?.map(elem => {
                let ImageNode: React.ReactNode = null;
                if (isAttachment(elem)) {
                    ImageNode = (
                        <ImageUploadPreview
                            key={elem.url}
                            canReorder={canReorder}
                            file={elem.url}
                            imageAlt="Attachment"
                            selectedTags={elem.tags}
                            size={size}
                            tagOptions={tagOptions}
                            onChangeTags={onChangeTags}
                            onRemove={onRemoveAttachment}
                        />
                    );
                }

                if (isFileType(elem)) {
                    ImageNode = (
                        <ImageUploadPreview
                            key={elem.file.name}
                            canReorder={canReorder}
                            file={elem.file}
                            imageAlt={elem.file.name}
                            progress={elem.progress}
                            selectedTags={elem.tags}
                            size={size}
                            tagOptions={tagOptions}
                            uploading={uploading}
                            onChangeTags={onChangeTags}
                            onRemove={handleRemoveImage}
                            onResize={onResizeImage}
                        />
                    );
                }

                if (isNativeImage(elem)) {
                    ImageNode = (
                        <ImageUploadPreview
                            key={elem.uri}
                            isNative
                            file={elem.uri}
                            imageAlt="Native Image"
                            size={size}
                            uploading={uploading}
                            onRemove={handleRemoveImage}
                        />
                    );
                }

                if (process.env.IS_WEB) {
                    const id = isAttachment(elem)
                        ? elem.url
                        : isFileType(elem)
                        ? elem.file.name
                        : elem.uri;
                    return (
                        <SortableItem
                            key={id}
                            className="w-1/3 flex-grow md:w-fit md:flex-grow-0"
                            dragable={canReorder}
                            id={id}
                        >
                            {ImageNode}
                        </SortableItem>
                    );
                }

                return (
                    <Box
                        key={
                            isAttachment(elem)
                                ? elem.url
                                : isFileType(elem)
                                ? elem.file.name
                                : elem.uri
                        }
                        className="w-1/3 grow md:grow-0 md:w-[165px]"
                    >
                        {ImageNode}
                    </Box>
                );
            })}
            {numOfImagesLeft > 0 && !!numOfImages ? (
                <>
                    <UploadImagePlaceholder
                        className="w-1/3 flex-grow md:flex-grow-0"
                        isDragging={isDragAccept}
                        size={size}
                        onOpen={onOpen}
                    />
                    {/* placeholder box to make UploadImagePlaceholder is same width with other images */}
                    {numOfImages % 2 === 0 && <Placeholder />}
                </>
            ) : (
                <>
                    {/* placeholder box to make odd images is same width with other images */}
                    {numOfImages % 2 === 1 && <Placeholder />}
                </>
            )}
        </Box>
    );

    const items = useMemo(
        () => [
            ...(attachmentOrFileList?.map(elem => ({
                id: isAttachment(elem) ? elem.url : isFileType(elem) ? elem.file.name : elem.uri,
                data: elem
            })) || [])
        ],
        [attachmentOrFileList]
    );

    const placeholderItems = useMemo(() => {
        const placeholderElement = [{id: "placeholder", data: null}];
        if (numOfImagesLeft > 0 && !!numOfImages) {
            return [
                {id: "uploadImagePlaceholder", data: null},
                ...(numOfImages % 2 === 0 ? placeholderElement : [])
            ];
        }
        return numOfImages % 2 === 1 ? placeholderElement : [];
    }, [numOfImages, numOfImagesLeft]);

    const initItems = useMemo(() => [...items, ...placeholderItems], [items, placeholderItems]);

    return (
        <VStack space="lg">
            {!!attachmentOrFileList?.length && (
                <>
                    {process.env.IS_WEB ? (
                        <Box
                            className={`
                            flex-row
                            flex-wrap
                            items-start
                            gap-4
                        `}
                        >
                            <SortableList
                                initItems={initItems}
                                renderItem={({id, data: elem}) => {
                                    let imageElem: React.ReactNode = null;

                                    if (elem === null) {
                                        if (id === "uploadImagePlaceholder") {
                                            return (
                                                <UploadImagePlaceholder
                                                    className="w-1/3 flex-grow md:flex-grow-0"
                                                    isDragging={isDragAccept}
                                                    size={size}
                                                    onOpen={onOpen}
                                                />
                                            );
                                        }
                                        if (id === "placeholder") {
                                            return <Placeholder />;
                                        }
                                        return null;
                                    }

                                    if (isAttachment(elem)) {
                                        imageElem = (
                                            <ImageUploadPreview
                                                key={elem.url}
                                                canReorder={canReorder}
                                                file={elem.url}
                                                imageAlt="Attachment"
                                                selectedTags={elem.tags}
                                                size={size}
                                                tagOptions={tagOptions}
                                                onChangeTags={onChangeTags}
                                                onRemove={onRemoveAttachment}
                                            />
                                        );
                                    }

                                    if (isFileType(elem)) {
                                        imageElem = (
                                            <ImageUploadPreview
                                                key={elem.file.name}
                                                canReorder={canReorder}
                                                file={elem.file}
                                                imageAlt={elem.file.name}
                                                progress={elem.progress}
                                                selectedTags={elem.tags}
                                                size={size}
                                                tagOptions={tagOptions}
                                                uploading={uploading}
                                                onChangeTags={onChangeTags}
                                                onRemove={handleRemoveImage}
                                                onResize={onResizeImage}
                                            />
                                        );
                                    }
                                    return (
                                        <SortableItem
                                            key={id}
                                            className="w-1/3 grow md:w-fit md:grow-0"
                                            dragable={canReorder}
                                            id={id}
                                        >
                                            {imageElem}
                                        </SortableItem>
                                    );
                                }}
                                strategy="horizontal"
                                onDragEnd={values => {
                                    const items = values
                                        .map(item => item.data)
                                        .filter(item => item !== null);
                                    if (isNative) {
                                        onSortEnd?.(filterAttachmentOrNativeImage(items));
                                    } else {
                                        onSortEnd?.(filterAttachmentOrFileType(items));
                                    }
                                }}
                            />
                        </Box>
                    ) : (
                        imageListNode
                    )}
                </>
            )}
            {!uploading && !numOfImages && (
                <UploadPlaceholder isDrag={isDragAccept} onOpen={onOpen} />
            )}
        </VStack>
    );
};

export default memo(ImageList);
